import React, { useEffect, useState } from 'react';
import { Building2, Home, Briefcase, Heart, Sun, User, Wrench, Briefcase as BusinessIcon, Map } from 'lucide-react';
export const WhoWeHelp = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeCard, setActiveCard] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      const element = document.getElementById('who-we-help');
      if (element) {
        const position = element.getBoundingClientRect();
        if (position.top < window.innerHeight - 100) {
          setIsVisible(true);
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check on mount
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  useEffect(() => {
    if (isVisible) {
      const interval = setInterval(() => {
        setActiveCard(prev => (prev + 1) % industries.length);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [isVisible]);
  const industries = [{
    name: 'Real Estate Investors',
    icon: <Building2 size={40} className="text-blue-light" data-id="element-362" />,
    description: 'Find high-value investment opportunities and potential sellers through our targeted campaigns.'
  }, {
    name: 'Wholesalers',
    icon: <Briefcase size={40} className="text-blue-light" data-id="element-363" />,
    description: 'Connect with motivated sellers and expand your property pipeline with our professional outreach services.'
  }, {
    name: 'Insurance Agencies',
    icon: <Heart size={40} className="text-blue-light" data-id="element-364" />,
    description: 'Reach potential clients who need coverage and grow your customer base with qualified leads.'
  }, {
    name: 'Roofing Companies',
    icon: <Home size={40} className="text-blue-light" data-id="element-365" />,
    description: 'Connect with property owners in need of roofing services and secure more installation contracts.'
  }, {
    name: 'Solar Installers',
    icon: <Sun size={40} className="text-blue-light" data-id="element-366" />,
    description: 'Identify homeowners interested in renewable energy solutions and boost your installation appointments.'
  }, {
    name: 'Real Estate Agents',
    icon: <User size={40} className="text-blue-light" data-id="element-367" />,
    description: 'Generate more listings and find buyers through targeted campaigns that deliver results.'
  }, {
    name: 'Fix and Flippers',
    icon: <Wrench size={40} className="text-blue-light" data-id="element-368" />,
    description: 'Discover distressed properties and motivated sellers to fuel your property flipping business.'
  }, {
    name: 'Business Owners',
    icon: <BusinessIcon size={40} className="text-blue-light" data-id="element-369" />,
    description: 'We provide thoroughly trained Virtual Assistants who can handle your needs with professionalism and expertise.'
  }, {
    name: 'Land Investors',
    icon: <Map size={40} className="text-blue-light" data-id="element-370" />,
    description: 'Specialized services for land investors to find off-market properties and motivated sellers in target areas.'
  }];
  return <section id="who-we-help" className="relative py-20 bg-gradient-to-b from-gray-900 to-black w-full" data-id="element-371">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden" data-id="element-372">
        <div className="absolute inset-0 opacity-5 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]" data-id="element-373"></div>
        {/* Animated circles */}
        <div className="absolute top-1/3 left-1/4 w-64 h-64 rounded-full bg-blue-primary/5 animate-pulse" style={{
        animationDuration: '6s'
      }} data-id="element-374"></div>
        <div className="absolute bottom-1/4 right-1/3 w-96 h-96 rounded-full bg-blue-light/5 animate-pulse" style={{
        animationDuration: '8s'
      }} data-id="element-375"></div>
      </div>
      <div className="container mx-auto px-4 relative z-10" data-id="element-376">
        <div className="text-center mb-16" data-id="element-377">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 animate-fadeIn" data-id="element-378">
            Who We{' '}
            <span className="text-blue-light relative" data-id="element-379">
              Help
              <span className="absolute -inset-1 bg-blue-light/10 blur-md -z-10 rounded-lg" data-id="element-380"></span>
            </span>
          </h2>
          <p className="text-gray-300 max-w-2xl mx-auto animate-fadeIn" style={{
          animationDelay: '0.2s'
        }} data-id="element-381">
            Our team delivers exceptional results across
            multiple industries. We specialize in generating high-quality leads
            for these business sectors:
          </p>
        </div>
        {/* Industry cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12" data-id="element-382">
          {industries.map((industry, index) => <div key={index} className={`bg-gray-900/80 border rounded-lg p-6 transition-all duration-500 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'} ${activeCard === index ? 'border-blue-light shadow-lg shadow-blue-primary/10 scale-[1.02]' : 'border-gray-800 hover:border-blue-light/50'} relative group cursor-pointer`} style={{
          transitionDelay: `${index * 100}ms`
        }} onClick={() => setActiveCard(index)} data-id="element-383">
              {/* Animated highlight on hover/active */}
              <div className={`absolute -inset-0.5 bg-gradient-to-r from-blue-primary to-blue-light rounded-lg blur ${activeCard === index ? 'opacity-20' : 'opacity-0 group-hover:opacity-10'} transition duration-500`} data-id="element-384"></div>
              <div className="relative flex flex-col items-center text-center" data-id="element-385">
                <div className={`mb-4 p-3 rounded-full ${activeCard === index ? 'bg-gradient-to-br from-blue-primary/20 to-blue-light/20' : 'bg-gray-800 group-hover:bg-gray-800/80'} transition-all duration-300`} data-id="element-386">
                  {activeCard === index && <div className="absolute inset-0 rounded-full bg-blue-primary/10 animate-ping opacity-75" data-id="element-387"></div>}
                  {industry.icon}
                </div>
                <h3 className={`text-xl font-bold mb-2 ${activeCard === index ? 'text-blue-light' : 'group-hover:text-blue-light/80'} transition-colors`} data-id="element-388">
                  {industry.name}
                </h3>
                <p className="text-gray-400 group-hover:text-gray-300 transition-colors" data-id="element-389">
                  {industry.description}
                </p>
              </div>
            </div>)}
        </div>
        {/* CTA Button */}
        <div className="text-center mt-8" data-id="element-390">
          <a href="#contact" className="inline-block px-8 py-3 bg-gradient-to-r from-blue-primary to-blue-light text-white font-bold rounded-md hover:from-blue-dark hover:to-blue-primary transition-all transform hover:scale-105 uppercase tracking-wide text-sm md:text-base relative overflow-hidden group" data-id="element-391">
            <span className="relative z-10" data-id="element-392">Grow Your Business Today</span>
            <span className="absolute top-0 left-0 w-full h-full bg-white/20 transform -translate-x-full group-hover:translate-x-0 transition-transform duration-300" data-id="element-393"></span>
          </a>
        </div>
      </div>
    </section>;
};